import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import type { MembersListResponse, StatusesResponse } from '@shared/api-responses';
import type { MemberListItem, MemberStatus } from '@shared/types';

const PAGE_SIZE = 100;

export default function Members() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'editor' || user?.role === 'admin' || user?.role === 'super_admin';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [currentOnly, setCurrentOnly] = useState(true);
  const [statuses, setStatuses] = useState<MemberStatus[]>([]);
  const [members, setMembers] = useState<MemberListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<StatusesResponse>('/statuses')
      .then((r) => setStatuses(r.statuses))
      .catch(() => {});
  }, []);

  // Any change to the query resets back to the first page.
  useEffect(() => {
    setPage(0);
  }, [search, status, currentOnly]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (status) params.set('status', status);
      if (currentOnly) params.set('current', 'true');
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      const r = await apiFetch<MembersListResponse>(`/members?${params.toString()}`);
      setMembers(r.members);
      setTotal(r.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [search, status, currentOnly, page]);

  // Debounce search/filter changes
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = page * PAGE_SIZE + members.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h1 className="text-xl font-bold text-dcvfd-dark">Members</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-zinc-500">
            {loading ? 'Loading…' : total === 0 ? 'No members' : `${rangeStart}–${rangeEnd} of ${total}`}
          </span>
          {canEdit && (
            <button
              onClick={() => navigate('/members/new')}
              className="rounded-lg bg-dcvfd-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              + New Member
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or CO ID…"
          className="flex-1 min-w-[220px] rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-dcvfd-accent"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s.code} value={s.code}>
              {s.code}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-zinc-600">
          <input type="checkbox" checked={currentOnly} onChange={(e) => setCurrentOnly(e.target.checked)} />
          Current active only
        </label>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}

      <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface-raised">
        <table className="w-full text-sm">
          <thead className="bg-surface text-zinc-500 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">Last</th>
              <th className="px-4 py-2 font-medium">First</th>
              <th className="px-4 py-2 font-medium">CO ID</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Batt</th>
              <th className="px-4 py-2 font-medium">Phone</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr
                key={m.co_id}
                onClick={() => navigate(`/members/${m.co_id}`)}
                className="border-t border-border-subtle hover:bg-surface cursor-pointer"
              >
                <td className="px-4 py-2 font-medium text-dcvfd-dark">{m.last_name}</td>
                <td className="px-4 py-2">{m.first_name}</td>
                <td className="px-4 py-2 text-zinc-500">{m.co_id}</td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-xs ${
                      m.is_current ? 'bg-dcvfd-accent/15 text-dcvfd-dark' : 'bg-zinc-100 text-zinc-500'
                    }`}
                  >
                    {m.status ?? '—'}
                  </span>
                </td>
                <td className="px-4 py-2">{m.battalion ?? ''}</td>
                <td className="px-4 py-2 text-zinc-600">{m.cell || m.phone || ''}</td>
              </tr>
            ))}
            {!loading && members.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                  No members match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between gap-3 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
            className="rounded-lg border border-border-default px-3 py-1.5 text-dcvfd-dark hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <span className="text-zinc-500">
            Page {page + 1} of {pageCount}
          </span>
          <button
            onClick={() => setPage((p) => (p + 1 < pageCount ? p + 1 : p))}
            disabled={page + 1 >= pageCount || loading}
            className="rounded-lg border border-border-default px-3 py-1.5 text-dcvfd-dark hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
